import { useMutation, useQuery } from "@apollo/client";
import { useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { supplyChainBooking } from "@/bookings/Booking";
import { BookingForm } from "@/bookings/BookingForm";
import { toBookingInputFields, toDuplicateFormData } from "@/bookings/BookingForm/mappers";
import { createSupplyChainBooking } from "@/bookings/BookingForm/mutations";
import { type BookingFormSchema } from "@/bookings/BookingForm/validation";
import { Spinner } from "@/components/Spinner";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useActiveBusinessUnit } from "@/hooks/use-active-business-unit";
import { useToast } from "@/hooks/use-toast";
import { extractErrorMessages, extractPayloadErrorMessages, getErrorMessage } from "@/lib/utils/errors";

interface DuplicateBookingDialogProps {
  bookingId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function DuplicateBookingDialog({ bookingId, open, onOpenChange }: DuplicateBookingDialogProps) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-[800px] max-h-[90vh] overflow-y-auto">
        <AlertDialogHeader>
          <AlertDialogTitle>Duplicate Booking</AlertDialogTitle>
          <AlertDialogDescription>
            All details are copied from the original booking. Documents aren&apos;t copied.
          </AlertDialogDescription>
        </AlertDialogHeader>
        {open ? <DuplicateBookingForm bookingId={bookingId} onClose={() => onOpenChange(false)} /> : null}
      </AlertDialogContent>
    </AlertDialog>
  );
}

function DuplicateBookingForm({ bookingId, onClose }: { bookingId: string; onClose: () => void }) {
  const { activeBusinessUnitId } = useActiveBusinessUnit();
  const navigate = useNavigate();
  const { toast } = useToast();

  const { data, loading, error } = useQuery(supplyChainBooking, {
    variables: { id: bookingId },
  });
  const [createBooking] = useMutation(createSupplyChainBooking, {
    refetchQueries: "active",
  });

  const booking = data?.supplyChainBooking ?? null;
  const loadFailed = !loading && (Boolean(error) || !booking);

  useEffect(() => {
    if (!loadFailed) return;

    toast({
      title: "Couldn't load booking to duplicate",
      description: error ? getErrorMessage(error) : "The booking could not be found.",
      variant: "destructive",
    });
    onClose();
  }, [loadFailed, error, toast, onClose]);

  const handleSubmit = async (values: BookingFormSchema) => {
    try {
      if (!activeBusinessUnitId) {
        toast({
          title: "Error",
          description: "Missing active business unit.",
          variant: "destructive",
        });

        return;
      }

      const result = await createBooking({
        variables: {
          input: {
            businessUnit: activeBusinessUnitId,
            ...toBookingInputFields(values),
          },
        },
      });

      const errors = result.errors || [];

      if (errors.length > 0) {
        toast({
          title: "Error duplicating booking",
          description: extractErrorMessages(errors),
          variant: "destructive",
        });

        return;
      }

      const payload = result.data?.createSupplyChainBooking;
      const payloadErrors = payload?.errors ?? [];
      const newBookingId = payload?.supplyChainBooking?.id;

      // The booking can come back null without any GraphQL error, so the dialog stays
      // open with the user's values rather than claiming a copy that doesn't exist
      if (payloadErrors.length > 0 || !newBookingId) {
        toast({
          title: "Error duplicating booking",
          description: extractPayloadErrorMessages(payloadErrors) || "The booking could not be created.",
          variant: "destructive",
        });

        return;
      }

      toast({
        variant: "default",
        title: "Booking duplicated successfully!",
      });

      onClose();

      void navigate({ to: "/bookings/$bookingId", params: { bookingId: newBookingId } });
    } catch (error: unknown) {
      toast({
        variant: "destructive",
        title: "Uh oh! Something went wrong.",
        description: getErrorMessage(error) || "There was a problem with your request.",
      });
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Spinner className="h-6 w-6" />
      </div>
    );
  }

  if (loadFailed) return null;

  return (
    <BookingForm
      mode="duplicate"
      onSubmit={handleSubmit}
      onCancel={onClose}
      initialData={toDuplicateFormData(booking)}
    />
  );
}
